import { faBars, faMagnifyingGlass, faSort, faSpinner, faTableCellsLarge, faXmark } from '@fortawesome/free-solid-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { useContext, useEffect, useRef, useState } from 'react'
import { twMerge } from 'tailwind-merge'
import { useOnClickOutside } from '~/utils/hooks/useOnClickOutside'
import { StakeListContext } from '../context'
import { useInventory } from '../hooks/useInventory'

/** `short` is what the trigger shows on mobile, where the full phrasing will not fit beside search. */
const SORT_OPTIONS: Array<{ value: InventorySort; label: string; short: string }> = [
    { value: 'id', label: 'ID (Low to High)', short: 'ID ↑' },
    { value: '-id', label: 'ID (High to Low)', short: 'ID ↓' },
    { value: '-rate', label: 'Rate (High to Low)', short: 'Rate ↓' },
    { value: 'rate', label: 'Rate (Low to High)', short: 'Rate ↑' },
]

export const InventoryControls = () => {
    return (
        <div className='flex flex-col lg:flex-row lg:items-center gap-3 lg:gap-4'>
            {/* Search and sort share a row on mobile — two short controls stacked wasted a band of
                vertical space above the grid. */}
            {/* Search keeps the first row to itself with Sort. The view toggle rode here briefly
                and squeezed the search field until its own placeholder truncated to "Search by
                IC"; it pairs with the status filter instead, which has spare width. */}
            <div className='flex flex-row gap-3 lg:contents'>
                <Search />
                <Sort />
            </div>
            <div className='flex flex-row gap-3 lg:contents'>
                <StatusFilter />
                <ViewToggle />
            </div>
            <div className='lg:ml-auto'>
                <BulkAction />
            </div>
        </div>
    )
}

const Search = () => {
    const { filter, setSearch } = useContext(StakeListContext)
    const [input, setInput] = useState(filter.search)

    useEffect(() => {
        const timer = setTimeout(() => setSearch(input), 300)
        return () => clearTimeout(timer)
    }, [input])

    return (
        <label className='flex-1 lg:max-w-xs flex flex-row items-center gap-3 border border-white/20 focus-within:border-[#C91FFF] rounded p-2 px-4 min-h-[2.75rem] min-w-0'>
            <FontAwesomeIcon icon={faMagnifyingGlass} className='text-white/40' />
            <input
                type='text'
                inputMode='numeric'
                placeholder='Search by ID'
                className='flex-1 bg-transparent outline-none font-bold placeholder:text-white/20 min-w-0'
                value={input}
                onChange={(e) => setInput(e.target.value)}
            />
            {input && (
                <button onClick={() => setInput('')} title='Clear' className='text-white/40 hover:text-white'>
                    <FontAwesomeIcon icon={faXmark} />
                </button>
            )}
        </label>
    )
}

const Sort = () => {
    const { filter, setSort } = useContext(StakeListContext)
    const [open, setOpen] = useState(false)
    const ref = useRef<HTMLDivElement>(null)

    useOnClickOutside(ref, () => setOpen(false))

    const current = SORT_OPTIONS.find((o) => o.value === filter.sort)

    return (
        <div ref={ref} className='relative shrink-0'>
            <button
                onClick={() => setOpen((o) => !o)}
                className='h-full w-full flex flex-row items-center gap-2 md:gap-3 border border-white/20 hover:bg-white/5 rounded p-2 px-3 md:px-4 font-bold uppercase text-sm whitespace-nowrap'
            >
                <FontAwesomeIcon icon={faSort} className='text-white/40' />
                <span className='md:hidden'>{current?.short}</span>
                <span className='hidden md:inline'>{current?.label}</span>
            </button>
            {open && (
                <div className='absolute right-0 md:left-0 top-full mt-2 z-20 flex flex-col bg-black border border-white/20 rounded p-2 min-w-max font-bold'>
                    {SORT_OPTIONS.map((option) => (
                        <button
                            key={option.value}
                            onClick={() => {
                                setSort(option.value)
                                setOpen(false)
                            }}
                            className={twMerge(
                                'text-left whitespace-nowrap p-2 rounded hover:bg-white/10 uppercase text-sm',
                                filter.sort === option.value && 'bg-white/20'
                            )}
                        >
                            {option.label}
                        </button>
                    ))}
                </div>
            )}
        </div>
    )
}

const StatusFilter = () => {
    const { filter, setStatus, scope } = useContext(StakeListContext)

    const activeCount = scope.filter((nft) => !!nft.staked).length
    const options: Array<[InventoryStatus, string, number]> = [
        ['all', 'All', scope.length],
        ['active', 'Active', activeCount],
        ['inactive', 'Inactive', scope.length - activeCount],
    ]

    return (
        // Three equal columns on mobile so the control fits the viewport instead of running off
        // the edge; natural widths from md up.
        <div className='grid flex-1 grid-cols-3 md:flex md:flex-row lg:flex-none lg:shrink-0 rounded overflow-hidden border border-white/20'>
            {options.map(([value, label, count]) => (
                <button
                    key={value}
                    onClick={() => setStatus(value)}
                    aria-pressed={filter.status === value}
                    className={twMerge(
                        // min-h keeps every segment a 44px tap target; padding alone gave 36px.
                        'px-2 md:px-4 py-2.5 min-h-[2.75rem] font-black uppercase text-xs md:text-sm truncate',
                        filter.status === value ? 'bg-white/20' : 'hover:bg-white/10 text-white/60'
                    )}
                >
                    {label} <span className='tabular-nums'>[{count}]</span>
                </button>
            ))}
        </div>
    )
}

/**
 * Always names exactly what it is about to do, and to how many items. It operates on the current
 * scope (category + search), not on the whole wallet — so filtering to Weapons and pressing it
 * cannot quietly activate your apes as well.
 */
const BulkAction = () => {
    const { filter, scope } = useContext(StakeListContext)
    const { useStake, useUnstake } = useInventory()
    const { stake, isStaking } = useStake()
    const { unstake, isUnstaking } = useUnstake()
    const isLoading = isStaking || isUnstaking

    const deactivating = filter.status === 'active'
    const targets = scope.filter((nft) => !!nft.staked === deactivating)

    if (targets.length === 0) return null

    return (
        <button
            disabled={isLoading}
            onClick={() => (deactivating ? unstake(targets) : stake(targets))}
            className={twMerge(
                'w-full flex flex-row items-center justify-center gap-3 px-8 py-2.5 min-h-[2.75rem] rounded',
                'font-black uppercase tracking-wide whitespace-nowrap disabled:opacity-50',
                deactivating ? 'bg-white/10 hover:bg-white/20' : 'bg-br1-gradient hover:brightness-125'
            )}
        >
            <span>
                {deactivating ? 'Deactivate' : 'Activate'} all <span className='tabular-nums'>[{targets.length}]</span>
            </span>
            {isLoading && <FontAwesomeIcon icon={faSpinner} spin />}
        </button>
    )
}

/**
 * Grid or list.
 *
 * Sits beside search and sort rather than in its own row, because it is the same class of control:
 * something you set once and forget. It defaults to list on a phone, where the grid shows about
 * five items per screen against the list's ten, and to grid on a desktop, where there is room for
 * the artwork and the grid is the nicer way to browse.
 */
const ViewToggle = () => {
    const { view, setView } = useContext(StakeListContext)

    const options: Array<[InventoryView, string, typeof faBars]> = [
        ['list', 'List view', faBars],
        ['grid', 'Grid view', faTableCellsLarge],
    ]

    return (
        <div className='flex shrink-0 flex-row rounded overflow-hidden border border-white/20'>
            {options.map(([value, label, icon]) => (
                <button
                    key={value}
                    onClick={() => setView(value)}
                    aria-pressed={view === value}
                    title={label}
                    aria-label={label}
                    className={twMerge(
                        'flex min-h-[2.75rem] w-11 items-center justify-center',
                        view === value ? 'bg-white/20' : 'hover:bg-white/10 text-white/50'
                    )}
                >
                    <FontAwesomeIcon icon={icon} />
                </button>
            ))}
        </div>
    )
}
